// Kanban view over the user's applications. Each column is one status; cards
// own the per-application summary (company logo, role, age, fit, alarms,
// counts). Drag-drop fires a status PATCH that flows through the per-app
// Durable Object — the optimistic update in `useUpdateApplicationStatus`
// reverts the move if the agent rejects the transition.

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { Bell, FileText, MessageSquare, UserCircle2 } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Pill, type PillTone } from "../components/ui/Pill";
import { EmptyState } from "../components/ui/EmptyState";
import { Skeleton } from "../components/ui/Skeleton";
import { Sheet } from "../components/ui/Sheet";
import { Input } from "../components/ui/Input";
import { ScorePill } from "../components/jobs/ScorePill";
import {
  ApplicationSheet,
  ageLabel,
  countdown,
} from "../components/applications/ApplicationSheet";
import {
  useApplications,
  useUpdateApplicationStatus,
  type ApplicationRow,
  type ApplicationStatus,
} from "../lib/applications";

// Open columns shown by default. Closed columns sit behind a toggle so the
// "active funnel" is the visual focus.
const OPEN_COLUMNS: ApplicationStatus[] = [
  "interested",
  "preparing",
  "approved",
  "applied",
  "interviewing",
  "offer",
];
const CLOSED_COLUMNS: ApplicationStatus[] = ["rejected", "withdrawn"];
const ALL_COLUMNS: ApplicationStatus[] = [...OPEN_COLUMNS, ...CLOSED_COLUMNS];

const STATUS_TONE: Record<ApplicationStatus, PillTone> = {
  interested: "neutral",
  preparing: "warning",
  approved: "accent",
  applied: "info",
  interviewing: "accent",
  offer: "success",
  rejected: "danger",
  withdrawn: "neutral",
};

const COLUMN_ACCENT: Record<ApplicationStatus, string> = {
  interested: "border-neutral-700",
  preparing: "border-warning-border",
  approved: "border-accent-700/60",
  applied: "border-info-border",
  interviewing: "border-accent-700/60",
  offer: "border-success-border",
  rejected: "border-danger-border",
  withdrawn: "border-neutral-700",
};

export default function Applications() {
  const [selected, setSelected] = useState<ApplicationRow | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);
  // ?status=<stage> deep-links from the /insights funnel land filtered.
  // Unknown values are ignored so a stale link still renders the board.
  const [params, setParams] = useSearchParams();
  const statusParam = params.get("status");
  const statusFilter = ALL_COLUMNS.includes(statusParam as ApplicationStatus)
    ? (statusParam as ApplicationStatus)
    : null;
  const q = useApplications(undefined, { includeCounts: true });
  const update = useUpdateApplicationStatus();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const grouped = useMemo(() => {
    const all = q.data?.applications ?? [];
    const needle = search.trim().toLowerCase();
    const filtered = needle
      ? all.filter((a) => {
          const haystack = [
            a.role,
            a.company_name ?? "",
            a.company_id ?? "",
            a.url,
          ]
            .join(" ")
            .toLowerCase();
          return haystack.includes(needle);
        })
      : all;

    const buckets: Record<ApplicationStatus, ApplicationRow[]> = {
      interested: [],
      preparing: [],
      approved: [],
      applied: [],
      interviewing: [],
      offer: [],
      rejected: [],
      withdrawn: [],
    };
    for (const a of filtered) buckets[a.status].push(a);
    for (const s of OPEN_COLUMNS.concat(CLOSED_COLUMNS)) {
      buckets[s].sort(byKanbanOrder);
    }
    return buckets;
  }, [q.data, search]);

  // The DragOverlay needs the dragged row to render the floating card.
  const draggingRow = useMemo(() => {
    if (!activeId) return null;
    return q.data?.applications.find((a) => a.id === activeId) ?? null;
  }, [activeId, q.data]);

  // Refresh the timestamp-driven "due in" countdown without re-fetching the
  // list. Once a minute is plenty — drag-drop + sheet panel still drive
  // their own freshness via tanstack-query.
  const [, forceRender] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => forceRender((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  function handleDragStart(e: DragStartEvent) {
    setActiveId(String(e.active.id));
  }

  function handleDragEnd(e: DragEndEvent) {
    const id = String(e.active.id);
    setActiveId(null);
    if (!e.over) return;
    const target = e.over.id as ApplicationStatus;
    const row = q.data?.applications.find((a) => a.id === id);
    if (!row || row.status === target) return;
    update.mutate({ id, status: target });
  }

  const visibleColumns = statusFilter
    ? [statusFilter]
    : showClosed
      ? ALL_COLUMNS
      : OPEN_COLUMNS;
  const closedCount = grouped.rejected.length + grouped.withdrawn.length;
  const totalShown = visibleColumns.reduce(
    (n, s) => n + grouped[s].length,
    0,
  );

  return (
    <>
      <section className="flex min-h-0 flex-col gap-4 p-4 sm:p-6 lg:p-8">
        <header className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Applications
            </h1>
            <p className="mt-1 text-sm text-neutral-400">
              Drag a card across columns to transition status. Click a card for
              the timeline + scheduled actions.
            </p>
          </div>
          <span className="text-xs text-neutral-500" aria-live="polite">
            {q.data ? (
              `${totalShown} shown`
            ) : q.isLoading ? (
              <Skeleton className="inline-block h-3 w-20" />
            ) : null}
          </span>
        </header>

        <div className="flex flex-wrap items-center gap-3">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search role or company…"
            className="max-w-xs"
          />
          {statusFilter ? (
            <span className="inline-flex items-center gap-2 text-xs text-neutral-400">
              Filtered to
              <Pill
                tone={STATUS_TONE[statusFilter]}
                variant="subtle"
                className="capitalize"
              >
                {statusFilter}
              </Pill>
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  setParams(
                    (prev) => {
                      prev.delete("status");
                      return prev;
                    },
                    { replace: true },
                  )
                }
              >
                Show all
              </Button>
            </span>
          ) : (
            <Button
              size="sm"
              variant={showClosed ? "primary" : "secondary"}
              onClick={() => setShowClosed((s) => !s)}
            >
              {showClosed ? "Hide closed" : `Show closed (${closedCount})`}
            </Button>
          )}
        </div>

        {q.isLoading ? (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <KanbanColumnSkeleton key={i} />
            ))}
          </div>
        ) : q.error ? (
          <EmptyState
            tone="danger"
            title="Couldn't load applications"
            description={String(q.error)}
            action={
              <Button variant="secondary" onClick={() => q.refetch()}>
                Retry
              </Button>
            }
          />
        ) : (q.data?.applications.length ?? 0) === 0 ? (
          <EmptyState
            title="Nothing tracked yet. Mark a job as applied from /jobs."
            action={
              <Button
                variant="secondary"
                onClick={() => {
                  window.location.href = "/jobs";
                }}
              >
                Browse jobs
              </Button>
            }
          />
        ) : (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveId(null)}
          >
            <div
              className="grid gap-3 overflow-x-auto pb-2"
              style={{
                // A lone filtered column shouldn't span the whole page.
                gridTemplateColumns: `repeat(${visibleColumns.length}, minmax(240px, ${
                  visibleColumns.length === 1 ? "420px" : "1fr"
                }))`,
              }}
            >
              {visibleColumns.map((status) => (
                <KanbanColumn
                  key={status}
                  status={status}
                  rows={grouped[status]}
                  onCardClick={(row) => setSelected(row)}
                  selectedId={selected?.id ?? null}
                />
              ))}
            </div>
            <DragOverlay dropAnimation={null}>
              {draggingRow ? (
                <div className="opacity-90 shadow-xl">
                  <ApplicationCard row={draggingRow} dragging selected={false} />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </section>

      <Sheet
        open={selected !== null}
        onOpenChange={(o) => {
          if (!o) setSelected(null);
        }}
      >
        <Sheet.Content side="right">
          {selected ? (
            <ApplicationSheet
              key={selected.id}
              application={selected}
              onClose={() => setSelected(null)}
              onDeleted={() => setSelected(null)}
              onStatusChange={(updated) =>
                setSelected((prev) =>
                  prev && prev.id === updated.id
                    ? { ...prev, ...updated }
                    : prev,
                )
              }
            />
          ) : null}
        </Sheet.Content>
      </Sheet>
    </>
  );
}

function KanbanColumn({
  status,
  rows,
  onCardClick,
  selectedId,
}: {
  status: ApplicationStatus;
  rows: ApplicationRow[];
  onCardClick: (row: ApplicationRow) => void;
  selectedId: string | null;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    <div
      ref={setNodeRef}
      className={`flex min-h-[200px] flex-col rounded-lg border bg-neutral-950/40 ${COLUMN_ACCENT[status]} ${
        isOver ? "ring-2 ring-accent-500/60" : ""
      }`}
    >
      <header className="flex items-center justify-between border-b border-neutral-800 px-3 py-2">
        <span className="inline-flex items-center gap-2">
          <Pill tone={STATUS_TONE[status]} variant="subtle" className="capitalize">
            {status}
          </Pill>
          <span className="text-[10px] text-neutral-500">{rows.length}</span>
        </span>
      </header>
      <div className="flex flex-1 flex-col gap-2 px-2 py-2">
        {rows.length === 0 ? (
          <p className="px-1 py-2 text-[11px] text-neutral-600">
            Drop cards here.
          </p>
        ) : (
          rows.map((row) => (
            <DraggableCard
              key={row.id}
              row={row}
              onClick={() => onCardClick(row)}
              selected={row.id === selectedId}
            />
          ))
        )}
      </div>
    </div>
  );
}

function DraggableCard({
  row,
  onClick,
  selected,
}: {
  row: ApplicationRow;
  onClick: () => void;
  selected: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: row.id,
  });
  // Hide the source card while dragging so the DragOverlay's clone is the
  // only visible representation. Without this you get a ghost + a clone.
  const style = isDragging ? { opacity: 0 } : undefined;
  return (
    <div ref={setNodeRef} style={style}>
      <ApplicationCard
        row={row}
        onClick={onClick}
        selected={selected}
        dragHandleProps={{ ...listeners, ...attributes }}
        dragging={false}
      />
    </div>
  );
}

interface CardProps {
  row: ApplicationRow;
  onClick?: () => void;
  selected: boolean;
  dragging: boolean;
  dragHandleProps?: Record<string, unknown>;
}

function ApplicationCard({
  row,
  onClick,
  selected,
  dragging,
  dragHandleProps,
}: CardProps) {
  const overdue =
    row.agent_state?.next_action_at !== null &&
    row.agent_state?.next_action_at !== undefined &&
    row.agent_state.next_action_at <= Date.now();

  // Click-vs-drag: dnd-kit's pointer activation constraint (4px) lets the
  // listener through for clicks, but we still prefer the explicit click
  // handler over the drag listeners' synthetic events.
  return (
    <button
      type="button"
      onClick={(e) => {
        // Don't treat the drag-end pointer up as a click.
        if (dragging) return;
        e.preventDefault();
        onClick?.();
      }}
      {...dragHandleProps}
      className={`group block w-full cursor-grab rounded-md border bg-neutral-900/60 p-3 text-left transition active:cursor-grabbing ${
        selected
          ? "border-accent-500/80 bg-neutral-900"
          : "border-neutral-800 hover:border-neutral-700 hover:bg-neutral-900"
      }`}
    >
      <div className="flex items-start gap-2">
        <CompanyLogo url={row.url} companyName={row.company_name} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-neutral-100">
            {row.role}
          </p>
          <p className="truncate text-[11px] text-neutral-500">
            {row.company_name ?? row.company_id ?? "—"}
            {row.company_tier ? ` · T${row.company_tier}` : null}
          </p>
        </div>
        <ScorePill score={row.score} />
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 text-[10px] text-neutral-500">
        <span>{ageLabel(row.updated_at)}</span>
        <span className="inline-flex items-center gap-2">
          {row.event_count > 0 ? (
            <span
              className="inline-flex items-center gap-0.5"
              title={`${row.event_count} timeline events`}
            >
              <MessageSquare size={10} /> {row.event_count}
            </span>
          ) : null}
          {row.contact_count > 0 ? (
            <span
              className="inline-flex items-center gap-0.5"
              title={`${row.contact_count} contacts`}
            >
              <UserCircle2 size={10} /> {row.contact_count}
            </span>
          ) : null}
          {row.draft_count > 0 ? (
            <span
              className="inline-flex items-center gap-0.5"
              title={`${row.draft_count} drafts`}
            >
              <FileText size={10} /> {row.draft_count}
            </span>
          ) : null}
        </span>
      </div>
      {row.agent_state?.next_action_at !== null &&
      row.agent_state?.next_action_at !== undefined ? (
        <div className="mt-2 inline-flex items-center gap-1 rounded border border-neutral-800 px-1.5 py-0.5 text-[10px]">
          <Bell
            size={10}
            className={overdue ? "text-warning-fg" : "text-neutral-400"}
          />
          <span className={overdue ? "text-warning-fg" : "text-neutral-400"}>
            {countdown(row.agent_state.next_action_at)}
          </span>
        </div>
      ) : null}
    </button>
  );
}

function CompanyLogo({
  url,
  companyName,
}: {
  url: string;
  companyName: string | null;
}) {
  const domain = useMemo(() => {
    try {
      return new URL(url).hostname.replace(/^www\./, "");
    } catch {
      return null;
    }
  }, [url]);
  if (!domain) {
    return (
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-neutral-800 text-[10px] font-semibold text-neutral-400">
        {(companyName ?? "?").slice(0, 1).toUpperCase()}
      </div>
    );
  }
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`}
      alt=""
      className="h-7 w-7 shrink-0 rounded bg-neutral-800 object-contain p-0.5"
      loading="lazy"
      onError={(e) => {
        // Hide on 404 — the alt text + sibling typography are enough.
        e.currentTarget.style.visibility = "hidden";
      }}
    />
  );
}

function KanbanColumnSkeleton() {
  return (
    <div className="flex min-h-[200px] flex-col rounded-lg border border-neutral-800 bg-neutral-950/40">
      <header className="border-b border-neutral-800 px-3 py-2">
        <Skeleton className="h-4 w-20" />
      </header>
      <div className="flex flex-1 flex-col gap-2 px-2 py-2">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    </div>
  );
}

function byKanbanOrder(a: ApplicationRow, b: ApplicationRow): number {
  // Within a column: surface "needs attention" first (overdue alarms),
  // then by next-action-at (soonest first), then by recency.
  const aNext = a.agent_state?.next_action_at ?? null;
  const bNext = b.agent_state?.next_action_at ?? null;
  const now = Date.now();
  const aOverdue = aNext !== null && aNext <= now;
  const bOverdue = bNext !== null && bNext <= now;
  if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;
  if (aNext !== null && bNext !== null) return aNext - bNext;
  if (aNext !== null) return -1;
  if (bNext !== null) return 1;
  return b.updated_at - a.updated_at;
}
